/**
 * useGuideMessages — message sequence processing for CoPilotGuide.
 *
 * Handles the typing animation pipeline, instantiation calls,
 * auto_transition, next_is_feature, and action display.
 */

import type { Chat, CoPilotGuideEngine, TransitionResult } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { apiErrorCode } from '@tonnie/foundation';
import { useErrorCapture } from '@tonnie/observability';
import { useCallback, useRef, type RefObject } from 'react';

import type { IMessage } from '../types';
import { widgetRegistry } from '../../../widgets/registry';
import type {
  GuideAnalyticsEventType,
  GuideTraceEventType,
  GuideTraceEvent,
  InstantiationState,
} from '../copilot-guide.types';

interface UseGuideMessagesArgs {
  engine: CoPilotGuideEngine;
  mountedRef: RefObject<boolean>;
  safeTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  setChatMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setActions: React.Dispatch<React.SetStateAction<import('@tonnie/copilot').Action[]>>;
  setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
  setInstantiationState: React.Dispatch<React.SetStateAction<InstantiationState | null>>;
  setCurrentChat: React.Dispatch<React.SetStateAction<Chat | null>>;
  setCurrentFeature: React.Dispatch<
    React.SetStateAction<{
      chatId: string;
      feature: string;
      config: Record<string, unknown>;
      displayMode: 'blended' | 'modal';
    } | null>
  >;
  processChatRef: RefObject<((chat: Chat) => void) | null>;
  emitEvent: (type: GuideAnalyticsEventType, meta?: Record<string, unknown>) => void;
  emitTrace: (
    type: GuideTraceEventType,
    extra?: Partial<Omit<GuideTraceEvent, 'id' | 'type' | 'timestamp'>>
  ) => void;
  updateProgress: () => void;
  onChatChange?: (chatId: string, variables: Record<string, unknown>) => void;
  onInstantiate?: (
    variables: Record<string, unknown>,
    context: Record<string, unknown>
  ) => Promise<unknown>;
  /**
   * Fired when a transition resolves to an engine sentinel (`terminal`
   * / `escape`) — i.e. `result.shouldClose === true`. The host wires
   * this to the same finalize/redirect logic the action handler uses,
   * so an `auto_transition: terminal` (or `escape`) actually exits the
   * flow instead of silently doing nothing.
   */
  onSentinelTransition?: (result: TransitionResult) => void;
  initialVariables: Record<string, unknown>;
}

export function useGuideMessages({
  engine,
  mountedRef,
  safeTimeout,
  setChatMessages,
  setIsLoading,
  setIsAnimating,
  setActions,
  setAnnouncement,
  setInstantiationState,
  setCurrentChat,
  setCurrentFeature,
  processChatRef,
  emitEvent,
  emitTrace,
  updateProgress,
  onChatChange,
  onInstantiate,
  onSentinelTransition,
  initialVariables,
}: UseGuideMessagesArgs) {
  const { t } = useTranslation();
  const { captureException } = useErrorCapture();

  // ── Sequence generation token ───────────────────────────────────────
  // Each `processMessageSequence` call claims a new generation. Every
  // async continuation (typing-flip timers, recursive `processNext`,
  // auto-transition hops) re-checks it and bails the instant a NEWER
  // sequence has started. This makes the animation pipeline robust to
  // re-entry: the start/restore effect can legitimately fire twice (e.g.
  // a freshly-created `copilot_guide_states` row arriving from the server
  // flips a query dep `undefined → 0`, or React StrictMode double-invokes
  // effects in dev), spawning a second chain for the same chat. Without
  // this guard the two chains race through shared `setChatMessages`: the
  // dedup-by-content branch REPLACES a bubble with a new `uniqueId`, so
  // the older chain's flip timer (keyed on the stale id) silently misses
  // and the bubble is stranded `isTyping: true` forever — its action
  // buttons never render and the flow dead-ends. Cancelling the stale
  // chain here means only the latest sequence mutates state.
  const sequenceGenRef = useRef(0);

  // ── Process Embedded Feature ──
  const processEmbeddedFeature = useCallback(
    (chat: Chat) => {
      const feature = chat.metadata?.feature;
      if (!feature) return;
      const staticConfig = (chat.metadata?.config ?? {}) as Record<string, unknown>;
      const engineVars = engine.getState().variables;
      const featureConfig = { ...engineVars, ...staticConfig };
      const displayMode = widgetRegistry.getDisplayMode(feature, 'blended');
      setCurrentFeature({ chatId: chat.id, feature, config: featureConfig, displayMode });
      setIsLoading(false);
      setActions([]);
      emitEvent('widget_opened', { chatId: chat.id, widgetName: feature });
      emitTrace('widget_opened', {
        chatId: chat.id,
        widgetName: feature,
        data: { displayMode, configKeys: Object.keys(featureConfig) },
      });
    },
    [emitEvent, emitTrace, engine, setCurrentFeature, setIsLoading, setActions]
  );

  // ── Process Message Sequence ──
  const processMessageSequence = useCallback(
    (chat: Chat) => {
      if (!chat.messages || chat.messages.length === 0) return;
      setIsLoading(false);
      setActions([]);
      setIsAnimating(true);

      // Claim this generation; any prior in-flight chain is now stale.
      const myGen = ++sequenceGenRef.current;
      const isStale = () => !mountedRef.current || sequenceGenRef.current !== myGen;

      const processNext = (index: number) => {
        if (isStale()) return;
        if (index >= (chat.messages ?? []).length) {
          setIsAnimating(false);
          const meta = chat.metadata as Record<string, unknown> | undefined;
          const effects = meta?.effects as string[] | undefined;
          const hasCallInstantiation =
            Array.isArray(effects) &&
            effects.includes('call_instantiation') &&
            meta?.instantiation &&
            onInstantiate;

          if (hasCallInstantiation && meta) {
            const vars = engine.getState().variables;
            const context = initialVariables ?? {};
            const inst = meta.instantiation as { resultVariable?: string } | undefined;
            const resultVariable = inst?.resultVariable ?? 'relocationKit';
            const delayMs = (meta.delay as number) ?? 0;
            const autoTransition = (meta.auto_transition as string) ?? '';
            const instantiationStartMs = Date.now();
            setInstantiationState({
              status: 'processing',
              startedAt: instantiationStartMs,
              kit: null,
              chatId: chat.id,
              autoTransition,
              errorCode: null,
            });
            emitTrace('instantiation_started', {
              chatId: chat.id,
              data: { variables: vars, context, resultVariable },
            });

            safeTimeout(() => {
              const promise = onInstantiate?.(vars, context);
              if (promise) {
                promise
                  .then((response) => {
                    if (!mountedRef.current) return;
                    emitTrace('instantiation_succeeded', {
                      chatId: chat.id,
                      durationMs: Date.now() - instantiationStartMs,
                      data: response,
                    });
                    emitTrace('variables_snapshot', {
                      data: { ...engine.getState().variables, [resultVariable]: response },
                    });
                    engine.setVariable(resultVariable, response);
                    setInstantiationState((prev) =>
                      prev ? { ...prev, status: 'complete', kit: response } : prev
                    );
                  })
                  .catch((err) => {
                    if (!mountedRef.current) return;
                    captureException(err instanceof Error ? err : new Error(String(err)), {
                      component: 'CoPilotGuide',
                      action: 'instantiate',
                    });
                    const errorMsg = err instanceof Error ? err.message : String(err);
                    emitTrace('instantiation_failed', {
                      chatId: chat.id,
                      durationMs: Date.now() - instantiationStartMs,
                      error: errorMsg,
                    });
                    setInstantiationState((prev) =>
                      prev ? { ...prev, status: 'error', errorCode: apiErrorCode(err) } : prev
                    );
                  });
              }
            }, delayMs);
            return;
          }

          // next_is_feature -> auto-transition
          if (chat.metadata?.next_is_feature) {
            engine.completeChat(chat.id);
            const task = engine.getCurrentTask();
            if (task) {
              const idx = task.chats.findIndex((c) => c.id === chat.id);
              const next = task.chats[idx + 1];
              if (next && next.type === 'embeddedFeature') {
                engine.transition(next.id);
                safeTimeout(() => {
                  setCurrentChat(next);
                  onChatChange?.(next.id, engine.getState().variables);
                  emitEvent('widget_opened', {
                    chatId: next.id,
                    widgetName: next.metadata?.feature,
                  });
                  processChatRef.current?.(next);
                  safeTimeout(updateProgress, 100);
                }, 200);
                return;
              }
            }
          }

          // auto_transition
          if (chat.metadata?.auto_transition) {
            const delay = chat.metadata.delay ?? 0;
            emitTrace('auto_transition', {
              chatId: chat.id,
              data: { target: chat.metadata.auto_transition, delayMs: delay },
            });
            safeTimeout(() => {
              let result = engine.completeChat(chat.id);
              if (!result) result = engine.transition(chat.metadata?.auto_transition ?? '');
              // Sentinel handling: when auto_transition targets `terminal`
              // or `escape` the engine returns `{shouldClose: true,
              // shouldRedirect: '<target>', nextChatId: null}`. Without
              // this branch the host never reacted — the chat sat there
              // forever showing whatever "Redirecting…" line was on
              // screen. The action handler in `use-co-pilot-guide.ts`
              // already had this branch; mirroring it here closes the
              // gap so future flows that auto-finish (e.g. processing
              // screens that should fire-and-forget into terminal) work.
              if (result?.shouldClose && onSentinelTransition) {
                onSentinelTransition(result);
                return;
              }
              if (result?.nextChatId) {
                const next = engine.getCurrentChat();
                if (next) {
                  setCurrentChat(next);
                  onChatChange?.(next.id, engine.getState().variables);
                  emitEvent('chat_entered', { chatId: next.id });
                  processChatRef.current?.(next);
                  safeTimeout(updateProgress, 100);
                }
              }
            }, delay as number);
            return;
          }

          // Show actions
          if (chat.actions && chat.actions.length > 0) {
            setActions(chat.actions);
            setAnnouncement(
              t('copilot.actionsAvailable', {
                count: chat.actions.length,
              })
            );
          }
          return;
        }

        // Process single message
        const msg = (chat.messages ?? [])[index];
        const text = engine.replaceVariables(msg.text);
        const uniqueId = `${chat.id}_${msg.id}_${index}_${Date.now()}`;
        const chatMsg: IMessage = {
          id: uniqueId,
          type: msg.sender === 'assistant' ? 'bot' : 'user',
          content: text,
          timestamp: new Date(),
          isTyping: msg.sender === 'assistant',
        };

        setChatMessages((prev) => {
          const dupIndex = prev.findIndex((m) => m.content === text && m.type === chatMsg.type);
          if (dupIndex >= 0) {
            const updated = [...prev];
            updated[dupIndex] = chatMsg;
            return updated;
          }
          return [...prev, chatMsg];
        });

        const typingMs = Math.max((text.length / 30) * 1000, 400);
        safeTimeout(() => {
          if (isStale()) return;
          setChatMessages((prev) =>
            prev.map((m) => (m.id === uniqueId ? { ...m, isTyping: false } : m))
          );
          if (msg.sender === 'assistant') setAnnouncement(text);
          safeTimeout(() => processNext(index + 1), 100);
        }, typingMs);
      };

      processNext(0);
    },
    [
      engine,
      updateProgress,
      onChatChange,
      safeTimeout,
      emitEvent,
      emitTrace,
      t,
      initialVariables,
      onInstantiate,
      captureException,
      mountedRef,
      setIsLoading,
      setActions,
      setIsAnimating,
      setInstantiationState,
      setCurrentChat,
      setChatMessages,
      setAnnouncement,
      processChatRef,
    ]
  );

  // ── Main Process Chat Router ──
  const processChat = useCallback(
    (chat: Chat) => {
      setIsLoading(false);
      onChatChange?.(chat.id, engine.getState().variables);
      emitEvent('chat_entered', { chatId: chat.id, taskId: chat.metadata?.task });
      emitTrace('chat_entered', {
        chatId: chat.id,
        taskId: chat.metadata?.task as string | undefined,
        data: { type: chat.type, variables: engine.getState().variables },
      });
      if (chat.type === 'messageSequence' || chat.type === 'confirmation') {
        processMessageSequence(chat);
      } else if (chat.type === 'embeddedFeature') {
        processEmbeddedFeature(chat);
      }
    },
    [
      processMessageSequence,
      processEmbeddedFeature,
      engine,
      onChatChange,
      emitEvent,
      emitTrace,
      setIsLoading,
    ]
  );

  return { processChat, processEmbeddedFeature };
}
